import { FabricObject } from "@/ws";
import { fabric } from "fabric";
import FontFaceObserver from "fontfaceobserver";
import { debounce } from "lodash";
import { computed, ref, watch } from "vue";
import { DefaultCanvasDimension } from "vue-glcommonui";
import { useStore } from "vuex";
import {
  assignUniquePropertiesToNewlyCreatedCanvasObject,
  DEFAULT_TEXT_BOX_PROPS,
  FbEvents,
  FbObjectEvents,
  FbObjectOrigins,
  FbObjects,
  getFixedStrokeSizeProperties,
  isLessonBackgroundObject,
  serializeFabricElementWithCustomAttributes,
} from "@/utils/fabric-utils";
import { ControlTextState } from "@/views/teacher-class/components/whiteboard-palette/whiteboard-palette";
import { CanvasObjectModel, useRemoteRequests } from "@/hooks/use-remote-requests.";
import { MODULES, vuexName } from "@/store/utils";
import { MIN_ZOOM_RATIO } from "@/utils/constant";
import { TeacherState } from "@/store/room/interface";
import { XYCoords } from "@/store/annotation-v2/state";
import { determineObjectTypes, OperationTypes } from "@/utils/utils";

const FontDidactGothic = "Didact Gothic";
let FontLoader = new FontFaceObserver(FontDidactGothic);

// eslint-disable-next-line
fabric.Textbox.prototype._wordJoiners = /[]/;

const deserializeFabricObject = (item: FabricObject) => {
  const { fabricData, fabricId } = item;
  const fabricObject = JSON.parse(fabricData);
  fabricObject.objectId = fabricId;
  return fabricObject;
};
export interface IOnObjectModifiedCallbacks {
  handleEditObjectFromCanvas: (event: any) => void;
}

export interface IOnObjectCreatedCallbacks {
  handleAddObjectFromCanvas: (event: any) => void;
  isLaserActive?: () => boolean;
}

export interface IOnLineCompletedCallbacks {
  handleAddObjectFromCanvas: (event: any) => void;
}

export interface IOnSelectionChangedCallbacks {
  openTextControlPassively?: (event: any) => void;
}

export interface IOnTextBoxEditedCallbacks {
  handleEditObjectFromCanvas?: (event: any) => void;
}
export const useFabricObject = () => {
  // When teacher draw a line on the canvas, assign this variable as a unique ID to the line object
  const lineObjectId = ref("");
  const { getters, commit } = useStore();
  const { broadcastMessageUpdateCanvasObject, requestMoveZoomedSlide } = useRemoteRequests();
  const isTeacher = computed(() => getters[vuexName(MODULES.AUTH.GETTERS.GET_IS_TEACHER)]);
  const currentExposureItemMedia = computed(() => getters[vuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)]);
  const oneToOneId = computed(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
  const nextColor = ref("");
  const nextFontFamily = ref("");
  const nextFontSize = ref(0);
  const currentSelectionEnd = ref(-1);
  const currentSelectionStart = ref(-1);
  const isChangeImage = ref(false);
  const updateNextStyles = (state: ControlTextState) => {
    if (state.fill) {
      nextColor.value = state.fill;
    }
    if (state.fontSize) {
      nextFontSize.value = state.fontSize;
    }
    if (state.fontFamily) {
      nextFontFamily.value = state.fontFamily;
    }
  };
  const isTextBox = (options: any) => options?.target?.type === FbObjects.Textbox;
  const isGroup = (target: any) => target?.type === FbObjects.Group;
  const isPath = (target: any) => target?.type === FbObjects.Path;
  // Objects that should be synced to remote listeners when they are created/edited
  const syncableFabricTypes = [FbObjects.Path, FbObjects.Circle, FbObjects.Rect, FbObjects.Textbox];
  const editableSyncableTypes = [...syncableFabricTypes, FbObjects.Line];
  const handlePreventSendMsg = (options: any, isPrevent: boolean) => {
    if (!options?.target) return;
    options.target.preventSendMsg = isPrevent;
  };
  const isPreventSendMsg = (options: any) => options?.target?.preventSendMsg;
  watch(currentExposureItemMedia, (currentItem, prevItem) => {
    if (currentItem && prevItem) {
      if (currentItem.id !== prevItem.id) {
        isChangeImage.value = true;
      }
    }
  });

  watch(oneToOneId, (currentOneToOneId, prevOneToOneId) => {
    if (currentOneToOneId !== prevOneToOneId && isChangeImage.value) {
      isChangeImage.value = false;
    }
  });

  const isEditing = ref(false);
  // Define a new function to handle object selection
  const handleObjectSelection = (options: any, isTeacher: boolean) => {
    if (!isLessonBackgroundObject(options?.target) && !isTeacher) {
      options.target.selectable = false;
      options.target.evented = false;
    }
  };
  function lockAllObjectActions(target: any) {
    target.lockMovementX = true;
    target.lockMovementY = true;
    target.lockRotation = true;
    target.lockScalingX = true;
    target.lockScalingY = true;
    target.hoverCursor = "auto";
  }
  const onObjectCreated = (canvas: any, callbacks?: IOnObjectCreatedCallbacks) => {
    canvas.on(FbEvents.ObjectAdded, (options: any) => {
      handleObjectSelection(options, isTeacher.value);
      const target = options.target;

      // Handle Path objects - created by FabricJS drawing, need special setup
      if (target.type === FbObjects.Path) {
        setupPathObject(target);
      }

      // Skip callback if object is from Full Sync (already on server)
      // This prevents infinite loop: add → send → receive → add → send...
      // Also skips laser/pencil paths from Helper/Student (skipCallback = true)
      if (target.skipCallback) {
        return;
      }

      // Skip laser paths drawn by Teacher — synced via space.cursors, not saved to redis
      if (target.type === FbObjects.Path && callbacks?.isLaserActive?.()) {
        target.isLaserPath = true; // mark for getAllUndoableObjects to exclude
        return;
      }

      // All drawable objects trigger callback for backend sync
      // Note: Line is excluded - it's synced manually via onLineCompleted on mouse up
      if (syncableFabricTypes.includes(target.type)) {
        callbacks?.handleAddObjectFromCanvas?.(options);
      }
    });
  };

  /**
   * Trigger callback when Line drawing is completed (on mouse up)
   */
  const onLineCompleted = (lineObject: any, callbacks?: IOnLineCompletedCallbacks) => {
    if (!lineObject) return;
    callbacks?.handleAddObjectFromCanvas?.({ target: lineObject });
  };

  /**
   * Send Ably message to broadcast textbox changes immediately (no debounce)
   */
  const broadcastTextBoxUpdate = async (target: any) => {
    if (!target || target.type !== FbObjects.Textbox) return;

    // Skip when Ably is not in 'connected' state — prevents messages from being queued
    // and delivered stale to students after reconnect (causes desync with Redis state).
    // Using raw Ably connection state (not Vuex isConnected) to also catch the
    // "connected→connecting" window where Vuex hasn't updated yet but Ably is no longer ready.
    const manager = getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_ROOM_MANAGER)];
    const isAblyConnected = manager?.WSClient?.isAblyConnected ?? false;
    if (!isAblyConnected) {
      return;
    }

    // Prepare SVG string for serialization
    target.customAttributes = target.customAttributes || {};
    target.customAttributes.svgStr = target.toSVG();

    // Send Ably to broadcast to other users
    const payload: CanvasObjectModel = {
      id: target.objectId,
      type: determineObjectTypes(target.type),
      operation: OperationTypes.Edit,
      metadata: JSON.stringify(serializeFabricElementWithCustomAttributes(target)),
    };
    await broadcastMessageUpdateCanvasObject(payload);
  };

  /**
   * Debounced API call to save textbox to Redis (500ms debounce)
   */
  const debouncedSaveTextBoxToRedis = debounce(async (target: any, callback?: (event: any) => void) => {
    if (!target || target.type !== FbObjects.Textbox) return;

    // Prepare SVG string for serialization
    target.customAttributes = target.customAttributes || {};
    target.customAttributes.svgStr = target.toSVG();

    // Call API to save to Redis (if callback provided)
    callback?.({ target });
  }, 500);

  /**
   * Central function to sync textbox changes to remote users
   * 1. Sets customAttributes.svgStr for the target
   * 2. Calls API callback (handleEditObjectFromCanvas) to save to Redis (debounced 500ms)
   * 3. Sends Ably message to broadcast real-time changes (immediate, no debounce)
   */
  const syncTextBoxToRemote = async (target: any, callback?: (event: any) => void) => {
    if (!target || target.type !== FbObjects.Textbox) return;

    // Prepare SVG string for serialization
    target.customAttributes = target.customAttributes || {};
    target.customAttributes.svgStr = target.toSVG();

    // Call API to save to Redis (debounced)
    await debouncedSaveTextBoxToRedis(target, callback);

    // Send Ably to broadcast to other users (immediate)
    await broadcastTextBoxUpdate(target);
  };

  /**
   * Handle zoomed slide dragging - constrains movement within canvas boundaries
   * Calculates and dispatches new coordinates when slide is dragged while zoomed
   */
  const handleZoomedSlideDrag = async (canvas: any, options: any) => {
    const { target } = options.transform;
    const viewPortX = Math.abs(canvas.viewportTransform[4]);
    const viewPortY = Math.abs(canvas.viewportTransform[5]);
    const zoom = canvas.getZoom();
    const clipPathLeft = DefaultCanvasDimension.width - target.width;
    const clipPathTop = DefaultCanvasDimension.height - target.height;
    const originX = target.width / 2;
    const originY = target.height / 2;

    // Constrain horizontal movement
    if (target.width * zoom < DefaultCanvasDimension.width) {
      if (target.left !== DefaultCanvasDimension.width / 2) {
        target.left = DefaultCanvasDimension.width / 2;
      }
    } else {
      if (target.left - viewPortX / zoom > originX) {
        target.left = originX + viewPortX / zoom;
      } else {
        if (target.left + viewPortX / zoom < originX + clipPathLeft) {
          target.left = originX + clipPathLeft - viewPortX / zoom;
        }
      }
    }

    // Constrain vertical movement
    if (target.height * zoom < DefaultCanvasDimension.height) {
      if (target.top !== target.height / 2) {
        target.top = target.height / 2 + viewPortY / zoom;
      }
    } else {
      if (target.top - viewPortY / zoom > originY) {
        target.top = originY + viewPortY / zoom;
      } else {
        if (target.top + viewPortY / zoom < originY) {
          target.top = originY + clipPathTop - viewPortY / zoom;
        }
      }
    }

    target.setCoords();
    const coords = {
      x: Math.floor(target.left) ?? 0,
      y: Math.floor(target.top) ?? 0,
      viewPortX: Math.floor(viewPortX),
      viewPortY: Math.floor(viewPortY),
    };

    if (canvas.getZoom() !== 1) {
      await requestMoveZoomedSlide(coords);
      commit(vuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS), coords, { root: true });
    }
  };

  const onObjectModified = (canvas: any, callbacks?: IOnObjectModifiedCallbacks) => {
    canvas.on(FbEvents.ObjectModified, async (options: any) => {
      // Handle zoomed slide dragging
      if (options?.target?.type === FbObjects.Group && options.action === FbObjectEvents.Drag) {
        await handleZoomedSlideDrag(canvas, options);
        return;
      }

      // Handle textbox moved/resized
      if (options?.target?.type === FbObjects.Textbox) {
        if (options?.target.text === "") {
          canvas.remove(options.target);
          return;
        }

        if (!isChangeImage.value && !isPreventSendMsg(options)) {
          // Sync to remote: API (Redis) + realtime (broadcast)
          await syncTextBoxToRemote(options.target, callbacks?.handleEditObjectFromCanvas);
        }

        if (isPreventSendMsg(options)) {
          handlePreventSendMsg(options, false);
        }
      }

      if (isChangeImage.value) {
        isChangeImage.value = false;
      }

      // Handle Rect/Circle/Path/Line moved/resized
      if (options?.target?.type && editableSyncableTypes.includes(options.target.type) && options.target.type !== FbObjects.Textbox) {
        callbacks?.handleEditObjectFromCanvas?.(options);
      }
    });
  };

  /**
   * Sync textbox during live editing:
   * - Broadcasts Ably message immediately for real-time updates
   * - Debounces API call (500ms) to avoid flooding Redis
   */
  const syncTextBoxDuringEditing = async (target: any, callback?: (event: any) => void) => {
    if (!target || target.type !== FbObjects.Textbox) return;

    // Broadcast Ably message immediately
    await broadcastTextBoxUpdate(target);

    // Debounce API call to Redis
    await debouncedSaveTextBoxToRedis(target, callback);
  };

  const onTextBoxEdited = (canvas: any, callbacks?: IOnTextBoxEditedCallbacks) => {
    canvas.on(FbEvents.TextEditingExited, (options: any) => {
      if (options?.target.type === FbObjects.Textbox) {
        if (options?.target.text === "") {
          setTimeout(() => {
            canvas.remove(options.target);
          }, 0);
        }
      }
    });

    canvas.on(FbEvents.TextSelectionChanged, (options: any) => {
      currentSelectionEnd.value = options.target.selectionEnd;
      currentSelectionStart.value = options.target.selectionStart;
    });

    canvas.on(FbEvents.TextEditingEntered, (options: any) => {
      if (options?.target.type === FbObjects.Textbox) {
        options.target.set("cursorColor", nextColor.value);
        isEditing.value = true;
        options.target.setSelectionStart(0);
        options.target.setSelectionEnd(options.target.text.length);
      }
    });

    canvas.on(FbEvents.TextChanged, async (options: any) => {
      // adjust textbox size when remove characters
      if (options.target instanceof fabric.IText) {
        const maxLength = Math.max(...options.target.textLines.map((line: string) => line.length));
        options.target.set({ width: maxLength });
      }

      // Apply next styles to newly typed text
      let startIndex = -1;
      let endIndex = -1;
      if (nextColor.value) {
        if (currentSelectionEnd.value === currentSelectionStart.value) {
          if (options.target?.prevTextValue?.length < options.target?.text?.length) {
            startIndex = currentSelectionEnd.value;
            endIndex = options.target.selectionEnd;
          }
        } else {
          startIndex = currentSelectionStart.value;
          endIndex = options.target.selectionEnd;
        }
      }
      if (startIndex > -1 && endIndex > -1) {
        const selectedTextStyles = options.target.getSelectionStyles(startIndex, endIndex, true);
        if (
          selectedTextStyles?.some(
            (style: any) =>
              style && (style.fill !== nextColor.value || style.fontFamily !== nextFontFamily.value || style.fontSize !== nextFontSize.value),
          )
        ) {
          options.target.setSelectionStart(startIndex);
          options.target.setSelectionEnd(endIndex);
          options.target.setSelectionStyles({
            fill: nextColor.value,
            fontFamily: nextFontFamily.value,
            fontSize: nextFontSize.value,
          });
          options.target.setSelectionStart(options.target.selectionEnd);
          options.target.setSelectionEnd(options.target.selectionEnd);
        }
      }

      options.target.prevTextValue = options.target.text;
      currentSelectionEnd.value = options.target.selectionEnd;
      currentSelectionStart.value = options.target.selectionEnd;

      // Sync during typing: Ably immediate, API debounced
      await syncTextBoxDuringEditing(options?.target, callbacks?.handleEditObjectFromCanvas);
    });
  };

  //display the fabric items get from getRoomInfo API which save in vuex store
  const displayFabricItems = (canvas: any, items: FabricObject[]) => {
    for (const item of items) {
      const currentObjects = canvas.getObjects();
      const existing = currentObjects.find((obj: any) => obj.objectId === item.fabricId);
      if (existing) {
        const fabricObject = deserializeFabricObject(item);
        FontLoader = new FontFaceObserver(fabricObject.fontFamily);
        const { type } = fabricObject;
        switch (type) {
          case FbObjects.Textbox:
            FontLoader.load().then(() => {
              canvas.add(new fabric.Textbox("", fabricObject));
            });
            break;
          default:
            break;
        }
        return;
      }
      displayCreatedItem(canvas, item);
    }
  };

  //display the fabric item received from realtime TeacherCreateFabricObject/TeacherModifyFabricObject
  //which save in vuex store as lastFabricUpdated
  const displayCreatedItem = (canvas: any, item: FabricObject) => {
    const fabricObject = deserializeFabricObject(item);
    const { type } = fabricObject;
    FontLoader = new FontFaceObserver(fabricObject.fontFamily);
    switch (type) {
      case FbObjects.Textbox: {
        const emptyTextBox = canvas.getObjects().find((obj: any) => obj.type === FbObjects.Textbox && !obj.text);
        if (emptyTextBox) {
          canvas.remove(emptyTextBox);
        }
        FontLoader.load().then(() => {
          canvas.add(new fabric.Textbox("", fabricObject));
        });
        break;
      }
      default:
        break;
    }
  };

  const displayModifiedItem = (canvas: any, item: FabricObject, existingItem: any) => {
    const fabricObject = deserializeFabricObject(item);
    const { type } = fabricObject;
    switch (type) {
      case FbObjects.Textbox:
        //two lines below fix the bug the text not display when texts's width not equal the Box's width (it can be fabric issue)
        if (fabricObject.text.length === 1) fabricObject.width = 0;
        existingItem.set(fabricObject);
        canvas.renderAll();
        break;
      default:
        break;
    }
  };
  const updateSelectedObjectStyles = async (canvas: any, state: ControlTextState, callback?: (event: any) => void) => {
    updateNextStyles(state);
    const selectedObj = canvas.getActiveObject();
    if (selectedObj?.type === FbObjects.Textbox) {
      const styles = { fill: state.fill, fontFamily: state.fontFamily, fontSize: state.fontSize };
      const start = selectedObj.isEditing ? selectedObj.selectionStart : 0;
      const end = selectedObj.isEditing ? selectedObj.selectionEnd : selectedObj.text.length;
      selectedObj.setSelectionStyles(styles, start, end);
      selectedObj.set("cursorColor", state.fill);
      canvas.renderAll();

      // Sync style changes: API (Redis) + realtime (broadcast)
      await syncTextBoxToRemote(selectedObj, callback);
    }
  };

  const onObjectRemoved = (canvas: any) => {
    canvas.on(FbEvents.ObjectRemoved, () => {
      // Logger.log(FbEvents.ObjectRemoved, options?.target);
    });
  };

  const onObjectSelected = (canvas: any) => {
    canvas.on(FbEvents.ObjectSelected, (_options: any) => {
      // Logger.log(FbEvents.ObjectSelected, options?.target);
    });
  };

  const onSelectionChanged = (canvas: any, callbacks?: IOnSelectionChangedCallbacks) => {
    canvas.on(FbEvents.SelectionCreated, async (options: any) => {
      isTextBox(options) && handlePreventSendMsg(options, false);
      callbacks?.openTextControlPassively?.(options.target);
    });
    canvas.on(FbEvents.BeforeSelectionCleared, async (options: any) => {
      isTextBox(options) && handlePreventSendMsg(options, true);
    });
    canvas.on(FbEvents.SelectionUpdated, async (options: any) => {
      isTextBox(options) && handlePreventSendMsg(options, false);
      callbacks?.openTextControlPassively?.(options.target);
    });
  };

  const teacher = computed<TeacherState | undefined>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_TEACHER)]);

  /**
   * Setup object properties and add to canvas (for shapes/textbox created by code)
   */
  const assignAndAddObject = (canvas: any, fabricObject: any) => {
    assignUniquePropertiesToNewlyCreatedCanvasObject(fabricObject);
    canvas.add(fabricObject);
    canvas.setActiveObject(fabricObject);
  };

  /**
   * Setup properties for Path object (already added to canvas by FabricJS)
   */
  const setupPathObject = (pathObject: any) => {
    assignUniquePropertiesToNewlyCreatedCanvasObject(pathObject, lineObjectId.value);
    lockAllObjectActions(pathObject);
  };

  /**
   * Get center coordinates considering viewport transform and zoom
   */
  const getCenterCoordinates = (canvas: any) => {
    const vpTransform = canvas?.viewportTransform;
    const zoom = vpTransform?.[0] ?? MIN_ZOOM_RATIO;
    const canvasTranslateX = vpTransform?.[4] ?? 0;
    const canvasTranslateY = vpTransform?.[5] ?? 0;
    return {
      x: (DefaultCanvasDimension.width / 2 - canvasTranslateX) / zoom,
      y: (DefaultCanvasDimension.height / 2 - canvasTranslateY) / zoom,
    };
  };

  // ========== Object Creation Functions ==========

  const createTextBox = async (canvas: any, coords: { top: number; left: number }, customTextBoxProps: ControlTextState) => {
    updateNextStyles(customTextBoxProps);
    const textBox = new fabric.Textbox("", {
      ...DEFAULT_TEXT_BOX_PROPS,
      ...customTextBoxProps,
      ...coords,
    });
    textBox.prevTextValue = "";
    const ctx = canvas.lowerCanvasEl.getContext("2d");
    ctx.font = `${textBox.fontSize}px ${textBox.fontFamily}`;
    // "hg" is a string to measure the metrics of the font
    const metrics = ctx.measureText("hg");
    const descent = metrics.fontBoundingBoxDescent || 0;
    textBox.top = textBox.top + descent;
    assignAndAddObject(canvas, textBox);
    textBox.enterEditing();
    textBox.setSelectionStart(0);
    textBox.setSelectionEnd(textBox.text.length);
    // Add textbox to annotation-v2 store when user starts typing
    // Note: We'll actually add it in onTextBoxEdited when text is not empty
    return textBox;
  };

  const createCircle = (canvas: any, strokeColor: any, strokeWidth: any, oneAndOne: any) => {
    const { x, y } = getCenterCoordinates(canvas);
    const circle = new fabric.Circle({
      left: x,
      top: y,
      radius: 30,
      fill: "",
      stroke: strokeColor.value,
      strokeWidth: strokeWidth.value,
      id: teacher.value?.id,
      isOneToOne: oneAndOne.value || null,
      originX: FbObjectOrigins.Center,
      originY: FbObjectOrigins.Center,
      ...getFixedStrokeSizeProperties(),
    });
    assignAndAddObject(canvas, circle);
  };

  const createSquare = (canvas: any, strokeColor: any, strokeWidth: any, oneAndOne: any) => {
    const { x, y } = getCenterCoordinates(canvas);
    const square = new fabric.Rect({
      left: x,
      top: y,
      width: 50,
      height: 50,
      fill: "",
      stroke: strokeColor.value,
      strokeWidth: strokeWidth.value,
      id: teacher.value?.id,
      isOneToOne: oneAndOne.value || null,
      originX: FbObjectOrigins.Center,
      originY: FbObjectOrigins.Center,
      ...getFixedStrokeSizeProperties(),
    });
    assignAndAddObject(canvas, square);
  };

  const createLine = (canvas: any, point: XYCoords, strokeColor: string, strokeWidth: number, teacherId: string | undefined, oneAndOne: any) => {
    const line = new fabric.Line([point.x, point.y, point.x, point.y], {
      stroke: strokeColor,
      strokeWidth: strokeWidth,
      id: teacherId,
      isOneToOne: oneAndOne || null,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: "auto",
    });
    assignAndAddObject(canvas, line);
    return line;
  };

  return {
    onObjectCreated,
    onLineCompleted,
    createTextBox,
    onTextBoxEdited,
    onObjectModified,
    displayFabricItems,
    displayCreatedItem,
    displayModifiedItem,
    isEditing,
    nextColor,
    FontLoader,
    onObjectRemoved,
    onObjectSelected,
    updateSelectedObjectStyles,
    onSelectionChanged,
    updateNextStyles,
    isGroup,
    isTextBox,
    isPath,
    lineObjectId,
    createCircle,
    createSquare,
    createLine,
  };
};
